import {
  getCountries,
  getCountryCallingCode,
  CountryCode,
  validatePhoneNumberLength,
  isValidPhoneNumber,
} from "libphonenumber-js";

/**
 * Builds a map from dial code string (e.g. "1", "91", "966") to the first
 * matching ISO 3166-1 alpha-2 country code (e.g. "US", "IN", "SA").
 *
 * When multiple countries share a dial code (e.g. "+1" covers US, CA),
 * the first entry returned by libphonenumber-js is used (typically the most
 * commonly expected one).
 */
const dialCodeToIsoMap: Record<string, CountryCode> = {};

const MAJOR_COUNTRIES: Record<string, CountryCode> = {
  "1": "US",
  "44": "GB",
  "7": "RU",
  "358": "FI",
  "47": "NO",
};

// Map of lowercase country name to ISO code (e.g. "united states" -> "US")
const nameToIsoMap: Record<string, CountryCode> = {
  "usa": "US",
  "united states of america": "US",
  "uk": "GB",
  "u.k.": "GB",
  "u.s.a.": "US",
  "uae": "AE",
  "united arab emirates": "AE",
};

try {
  const regionNames = new Intl.DisplayNames(["en"], { type: "region" });
  getCountries().forEach((iso) => {
    const name = regionNames.of(iso);
    if (name) {
      nameToIsoMap[name.toLowerCase()] = iso;
    }
    const dialCode = getCountryCallingCode(iso);
    if (!dialCodeToIsoMap[dialCode] || MAJOR_COUNTRIES[dialCode] === iso) {
      dialCodeToIsoMap[dialCode] = iso;
    }
  });
} catch (e) {
  // Fallback for environments without Intl.DisplayNames
  getCountries().forEach((iso) => {
    const dialCode = getCountryCallingCode(iso);
    if (!dialCodeToIsoMap[dialCode] || MAJOR_COUNTRIES[dialCode] === iso) {
      dialCodeToIsoMap[dialCode] = iso;
    }
  });
}

/**
 * Converts a raw dial code / country-code dropdown value to an ISO country code.
 *
 * Accepts formats like:
 *  - "+91"          → "IN"
 *  - "91"           → "IN"
 *  - "India  (+91)" → "IN"  (the format produced by the country-code dropdown)
 *  - "US"           → "US"  (already an ISO code — returned as-is if valid)
 *
 * Falls back to "US" if nothing can be determined.
 */
export function dialCodeToIso(value: string | undefined | null): CountryCode {
  if (!value) return "US";

  const trimmed = value.trim();

  // Already looks like an ISO code (2 uppercase letters)
  if (/^[A-Z]{2}$/.test(trimmed) && dialCodeToIsoMap[getCountryCallingCode(trimmed as CountryCode)]) {
    return trimmed as CountryCode;
  }

  const lowerValue = trimmed.toLowerCase();
  
  // 1. Name matching (e.g. "Antigua (+1)" matches "Antigua & Barbuda")
  const nameMatch = lowerValue.match(/^([^(]+)/);
  if (nameMatch) {
    const extracted = nameMatch[1].trim();
    if (extracted.length > 2) {
      // First try exact match
      for (const [name, iso] of Object.entries(nameToIsoMap)) {
        if (name === extracted) return iso as CountryCode;
      }
      // Then try fuzzy (extracted is part of full name or vice-versa)
      for (const [name, iso] of Object.entries(nameToIsoMap)) {
        if (name.includes(extracted) || extracted.includes(name)) return iso as CountryCode;
      }
    }
  }

  // 2. Extract digits and use dialCodeToIsoMap
  const match = trimmed.match(/\+?(\d+)/);
  if (match) {
    const digits = match[1];
    
    // Default handle for dial code 1 to US if not already matched
    if (digits === "1") return "US";

    // Try longest-match first (e.g. "1868" before "1")
    for (let len = digits.length; len >= 1; len--) {
      const sub = digits.substring(0, len);
      if (dialCodeToIsoMap[sub]) {
        return dialCodeToIsoMap[sub];
      }
    }
  }

  return "US";
}

/** Minimal shape of a country-list entry (matches iCountryData's relevant fields). */
type CountryDataLite = { name?: string; iso?: string; code?: string };

/**
 * Resolves the ISO country code from a stored country-code value.
 *
 * When `countriesData` is provided, the SELECTED country's own `iso` is used
 * (matched by name) — this disambiguates shared dial codes (e.g. "+1" → US vs
 * CA vs PR, so a Canadian number is validated against CA, not US) and tolerates
 * every stored shape: dropdown "United States  (+1)" (NBSP), recent-traveler
 * autofill "United States  (+1)" (double space, no NBSP), "+1", or "US".
 *
 * An empty/blank value returns `undefined` (no country selected → invalid). We
 * intentionally do NOT fall through to `dialCodeToIso("")`, which defaults "" to
 * "US" and would let an unselected country validate against the US.
 */
export function resolveCountryIso(
  rawCountryCode: string | undefined | null,
  countriesData?: CountryDataLite[]
): CountryCode | undefined {
  if (!rawCountryCode?.trim()) return undefined;
  if (countriesData?.length) {
    const name = rawCountryCode
      .replace(/ /g, " ")
      .replace(/\s*\(\+?\d+\)\s*$/, "")
      .trim()
      .toLowerCase();
    if (name) {
      const hit = countriesData.find((c) => c?.name?.trim().toLowerCase() === name);
      if (hit?.iso) return hit.iso as CountryCode;
    }
  }
  return dialCodeToIso(rawCountryCode);
}

/**
 * Returns a specific error message if the phone number is invalid for the given
 * country. Returns an empty string if valid. This is the single source of truth
 * for phone validity (isValidPhoneForCountry delegates here).
 *
 * Uses strict libphonenumber-js `isValidPhoneNumber` — the number must be a
 * valid, dialable number for the SELECTED country's numbering plan (pattern +
 * length), not merely a plausible length. This is what catches a wrong
 * country-code paired with a number that doesn't fit that country.
 *
 * @param rawPhone       - The formatted phone value from the input (may include spaces/dashes)
 * @param rawCountryCode - The raw countryCode from form state ("+91", "India  (+91)", "IN")
 * @param countriesData  - Optional country list; when present, ISO comes from the
 *                         selected country's `iso` (accurate for shared dial codes)
 */
export function getPhoneErrorMessage(
  rawPhone: string | undefined | null,
  rawCountryCode: string | undefined | null,
  countriesData?: CountryDataLite[]
): string {
  if (!rawPhone?.trim()) return "Phone number is required";
  const iso = resolveCountryIso(rawCountryCode, countriesData);
  if (!iso) return "Please select a country code";
  try {
    const digits = rawPhone.replace(/\D/g, "");
    if (!digits) return "Phone number is required";

    // Specific length messages first (nicer UX), then the strict pattern check.
    const lengthResult = validatePhoneNumberLength(digits, iso);
    if (lengthResult === "TOO_SHORT") return "Phone number is too short";
    if (lengthResult === "TOO_LONG") return "Phone number is too long";
    if (lengthResult === "INVALID_COUNTRY") return "Invalid country code";
    if (lengthResult === "NOT_A_NUMBER") return "Phone number contains invalid characters";

    // Length is acceptable → strict dialable/pattern validity for the country.
    if (!isValidPhoneNumber(digits, iso)) return "Phone number is invalid for the selected country";

    return "";
  } catch {
    return "Phone number is invalid for the selected country";
  }
}

/**
 * Strictly validates a phone number for a given country using libphonenumber-js.
 * Delegates to getPhoneErrorMessage so validity and the displayed message can
 * never disagree. Empty phone, unselected country, or a number that isn't valid
 * for the selected country → false.
 *
 * @param rawPhone       - The formatted phone value from the input (may include spaces/dashes)
 * @param rawCountryCode - The raw countryCode from form state ("+91", "India  (+91)", "IN")
 * @param countriesData  - Optional country list for accurate ISO resolution
 */
export function isValidPhoneForCountry(
  rawPhone: string | undefined | null,
  rawCountryCode: string | undefined | null,
  countriesData?: CountryDataLite[]
): boolean {
  return getPhoneErrorMessage(rawPhone, rawCountryCode, countriesData) === "";
}

/**
 * Normalizes a mobile-number string to DIGITS ONLY with any LEADING zero(s)
 * removed — the single source used by every phone entry point (typed input,
 * recent-traveler autofill, browser autofill, persisted/programmatic state) so a
 * mobile number can never contain spaces/special characters or start with a 0.
 * Callers re-apply `AsYouType` formatting AFTER this (formatting is intentionally
 * kept — only the digit source is cleaned). Empty/nullish → "".
 */
export function sanitizePhoneDigits(value: string | null | undefined): string {
  return (value ?? "").replace(/\D/g, "").replace(/^0+/, "");
}
